import { TextField, useMediaQuery, useTheme } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuid } from "uuid";
import { FormFieldWrapper } from "../FormFieldWrapper/FormFieldWrapper";
import { autoCompleteStyles } from "../FormFieldWrapper/styles/autoCompleteStyles";
import { mergeRefs } from "../Utils/Refs";
/** InputText component */
export function InputText(props) {
    const { autofocus, label, multiLine, maxLines, minLength, maxLength, language, spellCheck, onChange, onFocus, onKeyDown, required, value, disabled, autoComplete, ariaAutoComplete, "aria-label": ariaLabel, "aria-labelledby": ariaLabelledBy } = props;
    const [errorProps, setErrorProps] = useState({ hasError: props.hasError ?? false, errorMessage: props.hasError ? props.errorMessage : "" });
    const [inputTextValue, setInputTextValue] = useState(value ?? "");
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const errorMessage = props.hasError ? props.errorMessage : "";
    //zero width space is no longer considered to be a whitespace character in unicode (https://github.com/jprichardson/string.js/issues/182) and so does not get removed by trim, not manually removing it atm.
    const isValuePresent = isInputValuePresent(value);
    const constructErrorProps = useCallback((errorText) => {
        if (errorText) {
            return { hasError: true, errorMessage: errorText };
        }
        else if (props.hasError) {
            return { hasError: props.hasError, errorMessage: errorMessage };
        }
        return { hasError: false, errorMessage: "" };
    }, [errorMessage, props.hasError]);
    useEffect(() => {
        setInputTextValue(value ?? "");
    }, [value]);
    useEffect(() => {
        setErrorProps(constructErrorProps(null));
    }, [props.hasError, errorMessage, constructErrorProps]);
    /**
     * Handler to capture free text input, repackage and trigger the onChangeHandler on Autocomplete. Required to make freeSolo option controlled. There's no direct way to capture textField value.
     * @param e ChangeEvent<HTMLInputElement|HTMLTextAreaElement>
     */
    const handleTextInput = (e) => {
        handleOnChange(e, "onChange", e.target.value);
    };
    /** OnChange handler for Autocomplete. Additionally manage required state */
    const handleOnChange = (e, reason, values) => {
        const error = basicInputValidator(values);
        setErrorProps(constructErrorProps(error));
        setInputTextValue(values ?? "");
        if (onChange) {
            onChange(e, values, reason);
        }
    };
    const handleOnFocus = (e) => {
        if (onFocus) {
            onFocus(e);
        }
    };
    /** OnKeyDown handler */
    const handleKeyDown = (e) => {
        if (onKeyDown) {
            onKeyDown(e);
        }
    };
    /**
     * Basic validator to ensure min/max length properties are honored.
     * @param value the input to validate against.
     * @returns error messages or null.
     */
    const basicInputValidator = (value) => {
        if (!value) {
            return null;
        }
        if (minLength && props.belowMinLengthErrorMessage && value.length < minLength) {
            return props.belowMinLengthErrorMessage;
        }
        if (maxLength && props.aboveMaxLengthErrorMessage && value.length > maxLength) {
            return props.aboveMaxLengthErrorMessage;
        }
        return null;
    };
    /**
     * Handler to capture navigating away from the text input. This is needed for the editable grid.
     * @param e React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>
     */
    const handleBlurInput = (e) => {
        if (e.target.value === "") {
            handleOnChange(e, "blur", null);
        }
        else {
            handleOnChange(e, "blur", e.target.value);
        }
    };
    const textRef = useRef(null);
    let allRefs = textRef;
    if (props.inputRef) {
        allRefs = mergeRefs(textRef, props.inputRef);
    }
    //do not calculate autocomplete styles on every re-render, this will ensure styles are only calculated when states change.
    const styles = React.useMemo(() => autoCompleteStyles({ isMobile, isFreeText: true, isValuePresent, required, label, multiLine, disabled }), [
        isMobile,
        isValuePresent,
        required,
        label,
        multiLine,
        disabled
    ]);
    const inputId = `${props.id ?? uuid()}-input`;
    return (React.createElement(FormFieldWrapper, { formFieldRef: textRef, isMobile: isMobile, isValuePresent: isValuePresent, inputId: inputId, ...props, ...errorProps },
        React.createElement(Autocomplete, { sx: [
                {
                    "& .MuiInputBase-root:hover:not(.Mui-disabled):before": {
                        borderBottom: "1px solid transparent",
                        WebkitTransition: "none"
                    },
                    "& .MuiAutocomplete-endAdornment": {
                        //Change the clearIcon positioning so it is always aligned with the bottom of the text field
                        top: "auto",
                        bottom: -4
                    }
                },
                styles.container,
                styles.autocomplete
            ], 
            //Add margin here and override the padding in the Textfield so the scrollbar appears correctly.
            style: { marginTop: "7px", marginBottom: "7px" }, freeSolo: true, disabled: disabled, options: [], id: inputId, getOptionLabel: (option) => option.toString(), 
            // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
            // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
            onChange: (event, value, reason) => handleOnChange(event, reason, value), inputValue: inputTextValue, value: inputTextValue ? inputTextValue : null, renderInput: (params) => (React.createElement(TextField, { inputRef: allRefs, onChange: (e) => handleTextInput(e), multiline: multiLine, ...(isMobile && { label: label }), ...params, variant: "filled", onBlur: (e) => handleBlurInput(e), onFocus: (e) => handleOnFocus(e), onKeyDown: (e) => handleKeyDown(e), autoFocus: autofocus, placeholder: props.placeholder, inputProps: {
                    ...params.inputProps,
                    role: "textbox",
                    name: ariaLabel,
                    style: { padding: 0 },
                    lang: language,
                    spellCheck: spellCheck,
                    autoComplete: autoComplete ?? "off",
                    "aria-autocomplete": ariaAutoComplete,
                    "aria-label": ariaLabel,
                    "aria-labelledby": ariaLabelledBy
                }, maxRows: maxLines })) })));
}
export const isInputValuePresent = (value) => !!(value && value.trim().length > 0);
// Default props
InputText.defaultProps = {
    disabled: false,
    required: false,
    multiLine: false,
    hasError: false,
    labelProps: {
        width: "180px",
        position: "left"
    },
    language: (navigator && (navigator.language || (navigator.languages.length > 0 && navigator.languages[0]))) || "en",
    spellCheck: true
};
